/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * Whole minutes from an env value, or `fallback` when it is empty, `null`, or
 * not a non-negative whole number — a typo in .env must not silently shut a desk.
 */
function minutes(value, fallback) {
  if (value === undefined || value === null || String(value).trim() === '') return fallback;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : fallback;
}

const envConfig = {
  appName: process.env.REACT_APP_NAME,
  description: process.env.REACT_APP_DESCRIPTION,
  organizationId: process.env.REACT_APP_ORG_ID,
  applicationId: process.env.REACT_APP_APP_ID,
  backendApiUrl: process.env.REACT_APP_BACKEND_API_URL,
  backendTokenUrl: process.env.REACT_APP_BACKEND_TOKEN_URL,
  clientKey: process.env.REACT_APP_CLIENT_KEY,
  clientSecret: process.env.REACT_APP_CLIENT_SECRET,
  isUserRegistrationAllowed: String(process.env.REACT_APP_IS_USER_REGISTRATION_ALLOWED).toLowerCase() === 'true',
  isUserLoginAllowed: String(process.env.REACT_APP_IS_USER_LOGIN_ALLOWED).toLowerCase() === 'true',
  showExploreButton: String(process.env.REACT_APP_SHOW_EXPLORE_BUTTON).toLowerCase() !== 'false',
  localPages: process.env.REACT_APP_LOCAL_PAGES,
  // Where a participant is invited to write about a payment of theirs (My
  // Transactions). Trimmed, and empty when unset -- the page hides the whole
  // support block rather than rendering a mailto: to nowhere.
  // supportEmail: (process.env.REACT_APP_SUPPORT_EMAIL || '').trim(),
  //
  // REPLACED BY THE CONTACT PAGE ITSELF. My Transactions no longer builds a
  // mailto:, which is dead on any machine with no mail client; it opens the
  // contact form in a modal, prefilled. So what this app needs to know is the
  // PAGE, not a mailbox -- the backend already holds the recipients for that
  // form (FORM_NOTIFY_EMAILS / FORM_NOTIFY_PAGE_SLUGS).
  //
  // A slug, never an application name, so this is portable: point it at
  // whichever page carries the contact form. Empty means no support channel is
  // offered at all, which is how the mailbox used to behave when unset.
  contactPageSlug: (process.env.REACT_APP_CONTACT_PAGE_SLUG || 'contact-us').trim(),
  fileServerUrl: process.env.REACT_APP_FILE_SERVER_URL || "http://localhost:5000",
  fileServerApiKey: process.env.REACT_APP_FILE_SERVER_API_KEY,
  wsUrl: process.env.REACT_APP_WS_URL || "ws://localhost:8000",
  // Native Android app deep link, used to hand `application` users back to the app after a
  // password reset. Leave the scheme/package empty to disable and keep pure web routing.
  androidAppScheme: process.env.REACT_APP_ANDROID_APP_SCHEME,
  androidAppPackage: process.env.REACT_APP_ANDROID_APP_PACKAGE,
  androidAppFallbackUrl: process.env.REACT_APP_ANDROID_APP_FALLBACK_URL,
  checkInOpensBeforeMin: minutes(process.env.REACT_APP_CHECKIN_OPENS_BEFORE_MIN, 60),
  checkInLateAfterMin: minutes(process.env.REACT_APP_CHECKIN_LATE_AFTER_MIN, null),
  checkOutClosesAfterEndMin: minutes(process.env.REACT_APP_CHECKOUT_CLOSES_AFTER_END_MIN, null),
  checkInRescanMin: minutes(process.env.REACT_APP_CHECKIN_RESCAN_MIN, 10),
  // Stops the CLOCK closing the participant's self check-in door: it still
  // opens at checkInOpensBeforeMin before the start, then stays offered after
  // the event's day has ended, however long ago. A draft or cancelled event is still refused — that is the event not
  // being real, which no switch overrides. Off unless the value is "true", so
  // every deployment that does not set it behaves exactly as before. The
  // organizer's desk does not read this and does not move with it.
  selfCheckInNoExpiry: String(process.env.REACT_APP_SELF_CHECKIN_NO_EXPIRY).toLowerCase() === 'true',
  staleTimeMs: 60 * 1000,
};

export default envConfig;
